import AccordionSummary from '@material-ui/core/AccordionSummary';
import { observer, useLocalStore } from 'mobx-react-lite';
import React, { useContext, useEffect, useRef } from 'react';
import 'react-h5-audio-player/lib/styles.css';
import { UsersContext } from '../../contexts/Users';
import CommentForm from '../common/CommentForm';
import CompletionPanel from '../common/CompletionPanel';
import EvidenceList from '../common/EvidenceList.js';
import MarkItemChecked from '../common/MarkItemChecked';
import StatusLight from '../common/StatusLight';
import MessageForm from '../messaging/MessageForm';
import * as CSS from './elements/StaffStudentTargets';

const StaffStudentTargets = observer(({ studentId }) => {
  const usersStore = useContext(UsersContext);

  const commentFormRef = useRef(null);

  const store = useLocalStore(() => ({
    evidenceIdToComment: null,
    targetIdBeingEvidenced: null,
    isAddingEvidence: false,
    targetIdToEvidence: null,
    selectEvidence(evidenceId, targetId, isAddingEvidence = false) {
      store.evidenceIdToComment = evidenceId;
      store.targetIdBeingEvidenced = targetId;
      store.isAddingEvidence = isAddingEvidence;
      store.targetIdToEvidence = null;
    },
    setTargetIdToEvidence(targetId) {
      store.targetIdToEvidence = targetId;
    },
    openTargetIds: [],
    setTargetOpen(targetId, isOpen) {
      store.openTargetIds = isOpen
        ? [...store.openTargetIds, targetId]
        : store.openTargetIds.filter((id) => id !== targetId);
    },
  }));

  useEffect(() => {
    if (store.evidenceIdToComment && commentFormRef.current) {
      commentFormRef.current.scrollIntoView({
        block: 'end',
        inline: 'center',
        behavior: 'smooth',
      });
    }
  }, [store.evidenceIdToComment]);

  const student = usersStore.getUserById(studentId);
  const loggedInUser = usersStore.getLoggedInUser();

  const targets = student.private.getOrderedPersonalTargets(
    loggedInUser.selectedCategory
  );

  return (
    <div data-testid="StaffStudentTargets">
      <CSS.ItemListContainer>
        <CSS.ItemList>
          {targets.map((target, index) => {
            const status = student.private.getItemStatus(
              target.targetId,
              'personalTargets'
            );
            const evidence = student.private.getEvidenceByTargetId(
              target.targetId
            );
            const hasUpdates = student.private.hasUpdatesForUser(
              target.targetId,
              'personalTargets',
              loggedInUser
            );
            const isOpen = store.openTargetIds.includes(target.targetId);
            return (
              <CSS.ItemContainer key={target.targetId} isTop={index === 0}>
                <MarkItemChecked
                  student={student}
                  itemId={target.targetId}
                  collection="personalTargets"
                  isOpen={isOpen}
                />
                <CSS.FullWidthUpdateDot
                  show={hasUpdates}
                  data-testid={`updates-${target.targetId}`}
                >
                  <CSS.ItemAccordion
                    expanded={isOpen}
                    onChange={(e, expanded) =>
                      store.setTargetOpen(target.targetId, expanded)
                    }
                  >
                    <AccordionSummary>
                      <CSS.ItemSummary>
                        <CSS.Circle>
                          <CSS.ItemIndex data-testid={`target-${index + 1}`}>
                            {index + 1}
                          </CSS.ItemIndex>
                        </CSS.Circle>
                        <CSS.ModuleItemInstructions>
                          {target.targetText}
                        </CSS.ModuleItemInstructions>
                        <StatusLight status={status} itemId={target.targetId} />
                      </CSS.ItemSummary>
                    </AccordionSummary>

                    <CSS.ItemDetails>
                      <CSS.ListContainer>
                        <CSS.DetailSection>
                          <CSS.DetailSectionHeader>
                            <CSS.DetailSectionTitle>
                              Evidence
                            </CSS.DetailSectionTitle>
                            {store.targetIdToEvidence !== target.targetId && (
                              <CSS.Button
                                $variant="secondary"
                                $size="sm"
                                onClick={() =>
                                  store.setTargetIdToEvidence(target.targetId)
                                }
                              >
                                Add evidence <CSS.PlusIcon />
                              </CSS.Button>
                            )}
                          </CSS.DetailSectionHeader>
                          <EvidenceList
                            evidence={evidence}
                            resource="target"
                            onEvidenceClick={(evidenceId) =>
                              store.selectEvidence(evidenceId, target.targetId)
                            }
                            targetId={target.targetId}
                            studentId={studentId}
                            isStaff
                          />
                          {store.targetIdBeingEvidenced === target.targetId ? (
                            <CommentForm
                              refProp={commentFormRef}
                              studentId={student.id}
                              targetId={store.targetIdBeingEvidenced}
                              evidenceId={store.evidenceIdToComment}
                              afterSubmit={() =>
                                store.selectEvidence(null, null)
                              }
                            />
                          ) : (
                            store.targetIdToEvidence === target.targetId && (
                              <CSS.DetailSection>
                                <CSS.DetailSectionHeader>
                                  <CSS.DetailSectionTitle as="h4">
                                    Adding evidence for {student.displayName}
                                  </CSS.DetailSectionTitle>
                                  <CSS.Button
                                    $variant="secondary"
                                    $size="sm"
                                    onClick={() =>
                                      store.setTargetIdToEvidence(null)
                                    }
                                  >
                                    Cancel adding evidence
                                  </CSS.Button>
                                </CSS.DetailSectionHeader>
                                <MessageForm
                                  isStaff={true}
                                  targetId={target.targetId}
                                  userToAddTo={student}
                                ></MessageForm>
                              </CSS.DetailSection>
                            )
                          )}
                        </CSS.DetailSection>
                        {/* pinned to the bottom of the screen while scrolling through the
                            evidence, so Mark as complete is always to hand (F9) */}
                        <CSS.StickySignOff>
                          <CSS.DetailSection>
                            <CSS.DetailSectionTitle>
                              Sign off
                            </CSS.DetailSectionTitle>
                            <CompletionPanel
                              student={student}
                              itemId={target.targetId}
                              collection="personalTargets"
                            />
                          </CSS.DetailSection>
                        </CSS.StickySignOff>
                      </CSS.ListContainer>
                    </CSS.ItemDetails>
                  </CSS.ItemAccordion>
                </CSS.FullWidthUpdateDot>
              </CSS.ItemContainer>
            );
          })}
        </CSS.ItemList>
      </CSS.ItemListContainer>
    </div>
  );
});

export default StaffStudentTargets;
